import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
  Switch,
  Tooltip,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth, API_BASE } from "./AuthContext";
import { Navigate } from "react-router-dom";

const ADMIN_EMAIL = "baltibronx@gmail.com";

interface AdminUser {
  id: number;
  email: string;
  name: string | null;
  picture: string | null;
  is_plus: number;
  stripe_customer_id: string | null;
  created_at: string;
}

export default function AdminPage() {
  const { user, loading, token } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [toggling, setToggling] = useState<number | null>(null);

  const authBase = API_BASE;

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    setFetching(true);
    setError(null);
    try {
      const res = await fetch(`${authBase}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setUsers(await res.json());
    } catch (e: any) {
      setError("Failed to load users: " + e.message);
    } finally {
      setFetching(false);
    }
  }, [token, authBase]);

  useEffect(() => {
    if (!loading && user?.email === ADMIN_EMAIL) {
      fetchUsers();
    }
  }, [loading, user, fetchUsers]);

  const handleTogglePlus = async (userId: number) => {
    if (!token) return;
    setToggling(userId);
    try {
      const res = await fetch(`${authBase}/admin/users/${userId}/toggle_plus`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const updated = await res.json();
      setUsers((prev) =>
        prev.map((u) => (u.id === updated.id ? { ...u, is_plus: updated.is_plus } : u))
      );
    } catch (e: any) {
      setError("Toggle failed: " + e.message);
    } finally {
      setToggling(null);
    }
  };

  if (loading) return null;
  if (!user || user.email !== ADMIN_EMAIL) return <Navigate to="/" replace />;

  const filtered = users.filter(
    (u) =>
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      (u.name ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const plusCount = users.filter((u) => u.is_plus).length;

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: 2, py: 4 }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Admin — Users
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {users.length} total &nbsp;·&nbsp; {plusCount} PS Plus
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        size="small"
        placeholder="Search by name or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 320 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {fetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: "1.5px solid #c8d8e8", borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f4f8fc" }}>
                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Joined</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  PS Plus
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Stripe ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((u) => (
                <TableRow
                  key={u.id}
                  hover
                  sx={{ "&:last-child td": { borderBottom: 0 } }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        src={u.picture ?? undefined}
                        alt={u.name ?? u.email}
                        sx={{ width: 28, height: 28, fontSize: 13 }}
                      >
                        {(u.name ?? u.email)[0].toUpperCase()}
                      </Avatar>
                      <Typography variant="body2">{u.name ?? "—"}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {u.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(u.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={u.is_plus ? "Revoke Plus" : "Grant Plus"}>
                      <span>
                        <Switch
                          size="small"
                          checked={!!u.is_plus}
                          disabled={toggling === u.id}
                          onChange={() => handleTogglePlus(u.id)}
                          color="primary"
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {u.stripe_customer_id ?? "—"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.disabled" }}>
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
